import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Play, Pause, Loader2, SkipBack, SkipForward, Heart, Bookmark, RotateCcw } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (u) => (u.startsWith("http") ? u : `${BACKEND}${u}`);

// Relie l'élément audio aux contrôles de la page (progression + saut de phrase).
function useAudioBridge(ref, { onProgress, controllerRef, fractionOf, setPlaying, ready }) {
  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    const onEnd = () => { setPlaying(false); onProgress(null); };
    const onTime = () => Number.isFinite(a.duration) && a.duration && onProgress(a.currentTime / a.duration);
    // Les enregistrements du navigateur (webm) n'annoncent pas leur durée : on la force.
    const fixDuration = () => {
      if (a.duration !== Infinity) return;
      a.addEventListener("durationchange", () => { a.currentTime = 0; }, { once: true });
      a.currentTime = 1e101;
    };
    a.addEventListener("ended", onEnd);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", fixDuration);
    controllerRef.current = {
      goTo: (i) => {
        const seek = () => { a.currentTime = fractionOf(i) * a.duration; a.play(); setPlaying(true); };
        if (Number.isFinite(a.duration) && a.duration) seek(); else { a.addEventListener("loadedmetadata", seek, { once: true }); a.load(); }
      },
    };
    return () => {
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", fixDuration);
      controllerRef.current = null;
    };
  }, [ref, onProgress, controllerRef, fractionOf, setPlaying, ready]);
}

function PlayButton({ playing, loading, onClick, testId }) {
  return (
    <div className="flex flex-col items-center gap-3">
      <button onClick={onClick} disabled={loading} data-testid={testId}
        className="w-24 h-24 rounded-full bg-emerald-700 text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform disabled:opacity-70">
        {loading ? <Loader2 className="w-11 h-11 animate-spin" /> : playing ? <Pause className="w-12 h-12" /> : <Play className="w-12 h-12 ml-1" />}
      </button>
      <span className="font-patient font-bold text-lg text-emerald-800">
        {loading ? "Préparation de la voix…" : playing ? "Écoute en cours…" : "Écouter la lecture"}
      </span>
    </div>
  );
}

export function AudioPlayer({ src, ...bridge }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  useAudioBridge(ref, { ...bridge, setPlaying, ready: src });
  const toggle = () => {
    const a = ref.current;
    if (playing) { a.pause(); setPlaying(false); } else { a.play(); setPlaying(true); }
  };
  return (
    <div data-testid="story-audio-player">
      <audio ref={ref} src={fullUrl(src)} preload="metadata" />
      <PlayButton playing={playing} onClick={toggle} testId="story-audio-toggle" />
    </div>
  );
}

export function PatientAudioPlayer({ patientId, text, controllerRef, fallback, ...bridge }) {
  const ref = useRef(null);
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  useAudioBridge(ref, { ...bridge, controllerRef, setPlaying, ready: url });

  const prepare = async () => {
    setLoading(true);
    try {
      const r = await api.post(`/patients/${patientId}/tts`, { text });
      setUrl(fullUrl(r.data.url));
      setTimeout(() => { if (ref.current) { ref.current.play(); setPlaying(true); } }, 60);
    } catch {
      if (fallback) { setFailed(true); toast("La voix habituelle est indisponible : lecture de secours prête."); }
      else toast.error("La lecture n'a pas pu être préparée. Réessayez.");
    }
    finally { setLoading(false); }
  };
  const toggle = () => {
    if (!url) return prepare();
    if (playing) { ref.current.pause(); setPlaying(false); } else { ref.current.play(); setPlaying(true); }
  };
  useEffect(() => {
    if (!url && !failed) controllerRef.current = { goTo: () => toast("Appuyez d'abord sur « Écouter la lecture ».") };
  }, [url, failed, controllerRef]);

  if (failed) return <div data-testid="story-device-voice-fallback">{fallback}</div>;

  return (
    <div data-testid="story-patient-audio-player">
      {url && <audio ref={ref} src={url} preload="auto" />}
      <PlayButton playing={playing} loading={loading} onClick={toggle} testId="story-patient-audio-toggle" />
    </div>
  );
}

const PACES = [{ id: "lent", label: "Lent", wpm: 60 }, { id: "moyen", label: "Moyen", wpm: 95 }, { id: "rapide", label: "Rapide", wpm: 140 }];

// Lecture muette : les phrases s'allument une à une au rythme choisi.
export function SilentReader({ sentences, current, setCurrent, controllerRef }) {
  const [playing, setPlaying] = useState(false);
  const [pace, setPace] = useState("moyen");
  const wpm = PACES.find((p) => p.id === pace).wpm;
  useEffect(() => {
    controllerRef.current = { goTo: (i) => { setCurrent(i); setPlaying(true); } };
    return () => { controllerRef.current = null; };
  }, [controllerRef, setCurrent]);
  useEffect(() => {
    if (!playing) return;
    if (current < 0) { setCurrent(0); return; }
    const words = (sentences[current] || "").split(/\s+/).filter(Boolean).length;
    const t = setTimeout(() => {
      if (current >= sentences.length - 1) { setPlaying(false); setCurrent(-1); } else setCurrent(current + 1);
    }, Math.max(2000, (words / wpm) * 60000));
    return () => clearTimeout(t);
  }, [playing, current, wpm, sentences, setCurrent]);
  return (
    <div className="flex flex-col items-center gap-4" data-testid="story-silent-reader">
      <button onClick={() => setPlaying((p) => !p)} data-testid="story-silent-toggle"
        className="w-24 h-24 rounded-full bg-amber-600 text-white flex items-center justify-center shadow-lg active:scale-95 transition-transform">
        {playing ? <Pause className="w-12 h-12" /> : <Play className="w-12 h-12 ml-1" />}
      </button>
      <span className="font-patient font-bold text-lg text-amber-800">{playing ? "Lecture en cours…" : "Lire en silence"}</span>
      <div className="flex items-center gap-2" data-testid="story-silent-pace">
        <span className="text-sm text-gray-500 mr-1">Rythme</span>
        {PACES.map((p) => (
          <button key={p.id} onClick={() => setPace(p.id)} data-testid={`story-pace-${p.id}`} aria-pressed={pace === p.id}
            className={`h-11 px-4 rounded-full border-2 font-patient font-bold transition-colors ${pace === p.id ? "bg-amber-600 border-amber-600 text-white" : "bg-white border-amber-200 text-amber-800"}`}>
            {p.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export const TEXT_SCALES = [0.8, 0.9, 1, 1.15, 1.3, 1.5, 1.75];
const SCALE_KEY = "passerelle-story-scale";
export const savedTextScale = () => {
  const v = parseFloat(localStorage.getItem(SCALE_KEY));
  return TEXT_SCALES.includes(v) ? v : 1;
};

export function TextSizeControl({ scale, setScale }) {
  const [saved, setSaved] = useState(savedTextScale);
  const i = TEXT_SCALES.indexOf(scale);
  const save = () => { localStorage.setItem(SCALE_KEY, String(scale)); setSaved(scale); toast.success("Taille enregistrée pour les prochaines lectures."); };
  const reset = () => { localStorage.removeItem(SCALE_KEY); setSaved(1); setScale(1); toast.success("Retour à la taille normale."); };
  const btn = "h-12 min-w-12 px-4 rounded-full bg-white border-2 border-emerald-200 text-emerald-800 font-patient font-bold transition-colors hover:bg-emerald-50 disabled:opacity-40";
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 mb-6" data-testid="story-text-size">
      <span className="text-sm text-gray-500 mr-1">Taille du texte</span>
      <button className={`${btn} text-base`} onClick={() => setScale(TEXT_SCALES[i - 1])} disabled={i <= 0} aria-label="Texte plus petit" data-testid="story-text-smaller">A−</button>
      <span className="w-14 text-center font-patient font-bold text-gray-700" data-testid="story-text-size-value">{Math.round(scale * 100)} %</span>
      <button className={`${btn} text-xl`} onClick={() => setScale(TEXT_SCALES[i + 1])} disabled={i >= TEXT_SCALES.length - 1} aria-label="Texte plus grand" data-testid="story-text-bigger">A+</button>
      {scale !== saved ? (
        <button className={`${btn} inline-flex items-center gap-2 text-sm`} onClick={save} data-testid="story-text-size-save"><Bookmark className="w-4 h-4" /> Garder cette taille</button>
      ) : saved !== 1 && (
        <button className={`${btn} inline-flex items-center gap-2 text-sm`} onClick={reset} data-testid="story-text-size-reset"><RotateCcw className="w-4 h-4" /> Taille normale</button>
      )}
    </div>
  );
}

export function SentenceNav({ current, total, onGo }) {
  const btn = "flex-1 min-w-0 min-h-14 sm:min-h-16 py-2 px-2 sm:px-3 rounded-full bg-white border-4 border-emerald-700/20 font-patient font-bold text-base sm:text-lg text-gray-700 inline-flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-40";
  return (
    <div className="sticky bottom-4 z-10 mt-8 flex gap-2 sm:gap-3 w-full max-w-md mx-auto bg-[#FAF7F2]/90 backdrop-blur rounded-full p-1.5 shadow-[0_10px_30px_rgb(0,0,0,0.08)]" data-testid="story-sentence-nav">
      <button className={btn} disabled={current <= 0} onClick={() => onGo(current - 1)} data-testid="story-prev-sentence">
        <SkipBack className="w-6 h-6 shrink-0" /> <span className="sm:hidden">Précédente</span><span className="hidden sm:inline">Phrase précédente</span>
      </button>
      <button className={btn} disabled={current >= total - 1} onClick={() => onGo(current + 1)} data-testid="story-next-sentence">
        <span className="sm:hidden">Suivante</span><span className="hidden sm:inline">Phrase suivante</span> <SkipForward className="w-6 h-6 shrink-0" />
      </button>
    </div>
  );
}

export function FavoriteButton({ active, onToggle, testId, big = false }) {
  return (
    <button onClick={(e) => { e.stopPropagation(); onToggle(); }} data-testid={testId} aria-pressed={active}
      aria-label={active ? "Retirer des favoris" : "Ajouter aux favoris"}
      className={`inline-flex items-center gap-2 rounded-full border-2 font-patient font-bold transition-colors ${big ? "h-12 px-5 text-lg" : "w-11 h-11 justify-center"} ${active ? "bg-rose-50 border-rose-300 text-rose-500" : "bg-white border-gray-200 text-gray-400 hover:text-rose-400"}`}>
      <Heart className={`w-5 h-5 ${active ? "fill-current" : ""}`} />
      {big && (active ? "Conte préféré" : "Ajouter aux favoris")}
    </button>
  );
}
